import React, { forwardRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slide,
  TextField,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { theme } from '../../../../../theme';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { emailTemplate, PannelMembersType, toTitleCase } from '../../CandidateContent/CandidateContent';
import CandidateTabContent from './CandidateTabContent';
import StakeholderTabContent from './StakeholderTabContent';
import { useParams } from 'react-router-dom';
import { getHiringFlowByJob, sendEmailsToStakeholders, updateCandidateEmail } from '../../candidateManagementApi';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { sanitizeContent } from '../../../../Common/Sanitize/sanitizeReactQuill';
import { validateEmail } from '../../../Validation/validation';
import { useWarningPop } from '../../../../../Context/WarningPopContext';
import { useLoadingBackdrop } from '../../../../../Context/LoadingBackdropContext';

const Transition = forwardRef((
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface NotifyCandidateProps {
  open: boolean;
  onClose: () => void;
  email: string | null;
  setEmail: (value: string) => void;
  candidateStatus: string | null;
  handleSendNotify: (status: string | null, EmailSubject: string, EmailBody: string, EmailNum: number | null) => void;
  candidateName: string | null;
  recruiterName: string | null;
  candidatePosition: string | null;
  emailData: emailTemplate[];
  candidateJobNum: number | null;
  pannelMembersdropdown: PannelMembersType[];
  selectedCandidateId: number | null;
  onEmailSave: (payLoad: any) => Promise<any>
}

export interface StatusDropdownType {
  CandidateStatusKey: string,
  StageOrder: number
}

const NotifyCandidate: React.FC<NotifyCandidateProps> = ({
  open,
  onClose,
  email,
  handleSendNotify,
  candidateStatus,
  candidateName,
  recruiterName,
  candidatePosition,
  emailData,
  candidateJobNum,
  pannelMembersdropdown,
  setEmail,
  selectedCandidateId,
  onEmailSave
}) => {
  const { tenant } = useParams<{ tenant: string }>();
  const [tabIndex, setTabIndex] = useState(0);
  const [message, setMessage] = useState<string>('');
  const [status, setStatus] = useState<string | null>(candidateStatus);
  const [subject, setSubject] = useState<string>('');
  const [emailNum, setEmailNum] = useState<number | null>(null);
  const [statusDropdown, setStatusDropdown] = useState<StatusDropdownType[]>([])
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>([])
  const [selectedPanelMembers, setSelectedPanelMembers] = useState<string[]>([])
  const { showSnackbar } = useSnackbar();
  const [editEmail, setEditEmail] = useState("")
  const [isEditEmail, setIsEditEmail] = useState(false)
  const [isChangedEmail, setIsChangedEmail] = useState(false)
  const [emailError, setEmailError] = useState("")
  const { openWarningPop } = useWarningPop();
  const { show, hide } = useLoadingBackdrop();



  const fetchHiringFlowByJob = async () => {
    const result = await getHiringFlowByJob(tenant, { "JobNum": candidateJobNum })
    setStatusDropdown(result)
  }

  useEffect(() => {
    if (open) {
      fetchHiringFlowByJob()
      setTabIndex(0)
    }

  }, [tenant, open])

  useEffect(() => {
    if (!open) return;

    const matchedTemplate = emailData.find((template: emailTemplate) => {
      if (tabIndex === 0) {
        return template.EmailKey === candidateStatus;
      } else if (tabIndex === 1) {
        return ["Panel Interview - Stakeholder Info", "Offer Released - Stakeholder Info"].includes(template.EmailKey);
      }
      return false;
    });

    if (matchedTemplate) {
      const replacedMessage = matchedTemplate.EmailBody
        .replace('@CandidateName', candidateName ?? '')
        .replace('@Recruiter', recruiterName ?? '')
        .replace('@JobTitle', candidatePosition ?? '');

      setMessage(replacedMessage);
      setSubject(`${matchedTemplate.EmailSubject} Notification`);
      setStatus(matchedTemplate.EmailSubject); // optional, based on usage
      setEmailNum(matchedTemplate.EmailNum);
    } else {
      setMessage('');
      setSubject('');
      setEmailNum(null);
    }
  }, [open, tabIndex]);

  useEffect(() => {
    if (open) {
      setEditEmail(email || "")
      setIsChangedEmail(false)
      setIsEditEmail(false)
    }
  }, [email, open, tabIndex])



  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedStatus = event.target.value;
    setStatus(selectedStatus);
    const matchedTemplate = emailData.find(
      (template) => template.EmailKey === selectedStatus
    );

    if (matchedTemplate) {
      setMessage(
        matchedTemplate.EmailBody
          .replace('@CandidateName', candidateName ?? '')
          .replace('@Recruiter', recruiterName ?? '')
          .replace('@JobTitle', candidatePosition ?? '')
      );
      setSubject(`${matchedTemplate.EmailSubject} Notification`);
      setEmailNum(matchedTemplate.EmailNum);
    } else {
      setMessage("No template found for this status.");
      setSubject("Default Notification");
    }
  };

  // Top of NotifyCandidate.tsx (inside the component)
  const stakeholderEmailTemplates = emailData.filter((template) =>
    ["Panel Interview - Stakeholder Info", "Offer Released - Stakeholder Info"].includes(template.EmailKey)
  );

  const handleSend = () => {
    handleSendNotify(status, subject, message, emailNum);
  };

  const sendEmailToStakeholder = async () => {
    if (selectedStakeholders.length === 0) {
      showSnackbar("Please select at least one stakeholder.", "error")
      return
    }
    const sanitizedValue = sanitizeContent(message)
    if (!sanitizedValue) {
      showSnackbar("Email body is empty after sanitization.", "error")
      return
    }

    if (!subject.trim()) {
      showSnackbar("Subject cannot be empty.", "error")
      return
    }
    try {

      show()
      const payLoad = {
        stakeholders: selectedStakeholders,
        message: message,
        subject: subject,
        emailNum: emailNum,
      }
      const response = await sendEmailsToStakeholders(tenant, payLoad)
      if (response) {
        showSnackbar("Emails has been sent to stakeholders.", "success")
        onClose();
      } else {
        showSnackbar("Failed to send Emails.Please try again.", "error")
        onClose();
      }
    } catch (error) {
      showSnackbar("Failed to send Emails.Please try again.", "error")
      console.log(error)
    } finally {
      hide();
    }


  }

  // ✅ Centralized handler
  const handleEmailChange = (newEmail: string) => {
    setEditEmail(newEmail);

    if (newEmail.trim() !== (email ?? "").trim()) {
      setIsChangedEmail(true);
    } else {
      setIsChangedEmail(false);
    }

    setEmailError("")
  };

  // ✅ Cancel handler → rollback to original email
  const handleUndoEmailEdit = () => {
    setEditEmail(email || "");       // reset to original
    setIsEditEmail(false);
    setIsChangedEmail(false);
    setEmailError("");
  };

  const handleConfirmSaveEmail = async () => {

    const payLoad = {
      CandidateEmail: editEmail.trim(),
      CandidateNum: selectedCandidateId
    }

    const result = await onEmailSave(payLoad)
    if (result) {
      setEmail(editEmail.trim());
      setIsEditEmail(false);
      setIsChangedEmail(false);
    }

  };

  const handleSaveEmail = () => {
    if (editEmail.trim() === "") {
      setEmailError("Email cannot be empty.");
      return
    }
    if (!validateEmail(editEmail.trim())) {
      setEmailError("Please enter a valid email address.");
      return
    }


    if (isChangedEmail) {
      openWarningPop(() => {
        handleConfirmSaveEmail();
      }, "This will update the candidate's email throughout the system. Do you want to proceed?");
      return
    }

    setEmail(editEmail.trim());
    setIsEditEmail(false);
    setIsChangedEmail(false);

  }

  return (
    <Dialog
      open={open}
      onClose={() => { }}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)", } }}
      PaperProps={{
        sx: {
          maxWidth: '22vw',
          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', p: '2vh 1.5vw 0px 1vw' }}>
        <Box
          color={theme.palette.secondary.main}
          sx={{
            border: '1px solid #EEEEEE',
            bgcolor: theme.palette.secondary.light,
            margin: '0.3vw',
            mr: '1vw',
            width: '3vw',
            height: '3vw',
            borderRadius: '1vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i className="fa-regular fa-bell"></i>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Notify</Typography>
          <Typography variant="caption">{toTitleCase(candidateName || '')}</Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          sx={{ position: 'relative', width: '2vw', height: '4vh' }}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ mb: '1.2vh', mt: '2vh' }} />

      {/* Body */}
      <DialogContent sx={{ overflowY: 'auto', pt: '0.56vw' }}>

        {/* Tabs */}
        <Tabs
          value={tabIndex}
          onChange={(_, newIndex) => setTabIndex(newIndex)}
          variant="fullWidth"
          sx={{
            width: '100%',
            mx: 'auto',
            mb: 2,
            minHeight: 'unset',
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: '8px',
            // backgroundColor: '',
            overflow: 'hidden',
            '& .MuiTabs-flexContainer': {
              position: 'relative',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.85rem',
              minHeight: 'unset',
              padding: '6px 12px',
              borderRadius: 0,
              color: '#344054',
              flex: 1,
              zIndex: 1,
              borderRight: '1px solid #D6BBFB',
              '&:last-of-type': {
                borderRight: 'none',
              },
            },
            '& .Mui-selected': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab disableRipple label="Candidate" />
          <Tab disableRipple label="Stakeholder" />
        </Tabs>




        {/* Candidate Tab Content */}
        {tabIndex === 0 && (
          <CandidateTabContent
            email={email}
            setEmail={setEmail}
            status={status}
            subject={subject}
            message={message}
            handleStatusChange={handleStatusChange}
            setSubject={setSubject}
            setMessage={setMessage}
            statusDropdown={statusDropdown}
            pannelMembersdropdown={pannelMembersdropdown}
            selectedPanelMembers={selectedPanelMembers}
            setSelectedPanelMembers={setSelectedPanelMembers}
            setStatus={setStatus}
            candidateStatus={candidateStatus}
            isEditEmail={isEditEmail}
            setIsEditEmail={setIsEditEmail}
            editEmail={editEmail}
            handleEmailChange={handleEmailChange}
            handleUndoEmailEdit={handleUndoEmailEdit}
            handleSaveEmail={handleSaveEmail}
            emailError={emailError}
          />
        )}

        {/* Stakeholder Tab Content */}
        {tabIndex === 1 &&
          <StakeholderTabContent
            email={email}
            status={status}
            subject={subject}
            message={message}
            handleStatusChange={handleStatusChange}
            setSubject={setSubject}
            setMessage={setMessage}
            statusDropdown={statusDropdown}
            pannelMembersdropdown={pannelMembersdropdown}
            selectedStakeholders={selectedStakeholders}
            setSelectedStakeholders={setSelectedStakeholders}
            emailData={stakeholderEmailTemplates} // ✅ only relevant templates passed
            setStatus={setStatus}
          />}
      </DialogContent>

      {/* Footer */}
      <Divider />
      <DialogActions sx={{ padding: '2vh' }}>
        <Button
          onClick={() => {
            if (tabIndex === 0) {
              handleSend()
            } else {
              sendEmailToStakeholder()
            }
          }
          } variant="contained" fullWidth
          disabled={tabIndex === 0 && isEditEmail}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotifyCandidate;
